import { Tag } from '@pay-com/components'
import { File, Logo, Signature } from '@pay-com/icons'
import { ExpandableSection } from 'components/ExpandableSection'
import { TaskItem } from 'components/Tasks/TaskItem'
import {
  Task,
  TaskStatus,
  useEntityOnboardingPricingListQuery,
  useTasksQuery
} from 'generated/graphql'
import { useOnboardingRegion, useSignedPricings } from 'hooks'
import { Individuals, MerchantApplicationData } from 'pages/Onboarding/types'
import { Amendment } from 'pages/Amendment'
import { AmendmentSuccess } from 'pages/Amendment/AmendmentSuccess'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mapStepIdToGqlCategory } from 'utils/map-step-to-category'
import { LocalStorageKeys, setLocalStorage } from 'utils/local-storage'
import { useAuthToken } from 'hooks/use-auth-token'

type ApplicationReviewProps = {
  merchantApplication: MerchantApplicationData
  individuals?: Individuals
}

export const ApplicationReview = ({
  merchantApplication,
  individuals
}: ApplicationReviewProps) => {
  const { t } = useTranslation()
  const { email } = useAuthToken()
  const steps = useOnboardingRegion({ merchantApplication })
  const [submittedTaskIds, setSubmittedTaskIds] = useState<Set<string>>(
    () => new Set()
  )
  const { signedIds } = useSignedPricings()
  const [hasJustSigned, setHasJustSigned] = useState(false)
  const handleTaskSubmitted = useCallback((taskId: string) => {
    setSubmittedTaskIds(prev => {
      if (prev.has(taskId)) return prev
      const next = new Set(prev)
      next.add(taskId)
      return next
    })
  }, [])

  const { data, loading } = useTasksQuery({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: {
      input: {
        limit: 100,
        filter: {
          in: {
            status: [TaskStatus.New]
          }
        },
        ignoreSelect: true
      }
    }
  })

  const {
    data: entityOnboardingPricingList,
    loading: entityOnboardingPricingListLoading
  } = useEntityOnboardingPricingListQuery()

  const pricings = useMemo(
    () =>
      entityOnboardingPricingList?.entityOnboardingPricingList
        ?.onboardingPricingList ?? [],
    [entityOnboardingPricingList]
  )

  useEffect(() => {
    if (entityOnboardingPricingListLoading) return
    if (!entityOnboardingPricingList) return
    if (signedIds.size === 0) return

    const validIds = new Set(pricings.map(p => p.id))
    const filtered = Array.from(signedIds).filter(id => validIds.has(id))

    if (filtered.length !== signedIds.size) {
      setLocalStorage(LocalStorageKeys.SignedPricings, filtered)
    }
  }, [
    entityOnboardingPricingListLoading,
    entityOnboardingPricingList,
    pricings,
    signedIds
  ])

  const unsignedPricings = pricings.filter(p => !signedIds.has(p.id))

  const nextUnsignedPricing = unsignedPricings[0]

  const taxSigner = individuals?.find(item =>
    item.parentRelations?.some(relation => relation.signsTaxDocuments)
  )?.individual

  const taxSignerEmail = taxSigner?.email
  const taxSignerFullName = `${taxSigner?.firstName} ${taxSigner?.lastName}`

  const primaryContactEmail = individuals?.find(item =>
    item.parentRelations?.some(relation => relation.isPrimaryContact)
  )?.individual?.email

  const primaryContactIsTaxSigner = taxSignerEmail === primaryContactEmail

  const tasks = (data?.tasks.tasks || []) as Task[]

  const categories = useMemo(
    () =>
      tasks.reduce<Record<string, Task[]>>((acc, curr) => {
        const category =
          curr.values?.find(value => value.key === 'category')?.value
            .stringValue || 'other'

        return {
          ...acc,
          [category]: [...(acc[category] || []), curr]
        }
      }, {}),
    [tasks]
  )

  const reviewSections = [
    ...steps.map(step => ({
      id: step.id,
      title: step.title,
      icon: step.stepIcon
    })),
    {
      id: 'other',
      title: t('applicationReview.other.title'),
      icon: <File style={{ height: 20 }} fill="black" />
    }
  ]

  if (loading || entityOnboardingPricingListLoading) {
    return (
      <>
        <div className="fixed left-0 top-0 z-10 flex p-6">
          <Logo />
        </div>
        <div className="grid h-screen place-content-center">
          <p className="text-gray-600">{t('applicationReview.loading')}</p>
        </div>
      </>
    )
  }

  const sectionStateConfig = {
    inReview: {
      tagVariant: 'secondary' as const,
      statusLabel: t('applicationReview.status.inReview'),
      description: t('applicationReview.section.inReviewDescription')
    },
    pending: {
      tagVariant: 'warning' as const,
      statusLabel: t('applicationReview.status.pending'),
      description: t('applicationReview.section.pendingDescription')
    }
  }

  const getSectionState = (hasPending: boolean) =>
    hasPending ? 'pending' : 'inReview'

  if (hasJustSigned && email === taxSignerEmail) {
    const hasMoreToSign = nextUnsignedPricing != null
    return (
      <AmendmentSuccess
        showButton={primaryContactIsTaxSigner || hasMoreToSign}
        hasMorePricings={hasMoreToSign}
        onContinue={() => setHasJustSigned(false)}
      />
    )
  }

  if (nextUnsignedPricing && email === taxSignerEmail) {
    return (
      <Amendment
        key={nextUnsignedPricing.id}
        pricingId={nextUnsignedPricing.id}
        taxSignerFullName={taxSignerFullName}
        onSigned={() => setHasJustSigned(true)}
      />
    )
  }

  if (email === taxSignerEmail && !primaryContactIsTaxSigner) {
    return <AmendmentSuccess showButton={primaryContactIsTaxSigner} />
  }

  return (
    <>
      <div className="fixed left-0 top-0 z-10 flex p-6">
        <Logo />
      </div>
      <div className="grid min-h-dvh justify-center bg-white pb-20">
        <div className="mt-20 flex flex-col p-6 lg:mt-6 lg:max-w-[37.5rem] lg:px-4 lg:py-6 lg:pr-6">
          <h1 className="text-2xl font-semibold text-blueGray-900">
            {t('applicationReview.title')}
          </h1>
          <p className="mt-3 text-gray-600">
            {t('applicationReview.description')}
          </p>
          <div className="mt-7 grid gap-3">
            {unsignedPricings.length > 0 && (
              <ExpandableSection
                title={t('applicationReview.merchantProcessing.title')}
                startIcon={<Signature style={{ height: 20 }} fill="black" />}
                expandable={false}
                open={false}
                expandButtonLabel={
                  <Tag size="small" variant="warning">
                    {t('applicationReview.status.pending')}
                  </Tag>
                }
                secondaryTitle={t(
                  'applicationReview.merchantProcessing.pendingDescription'
                )}
              >
                {null}
              </ExpandableSection>
            )}
            {reviewSections.map(section => {
              const gqlCategory = mapStepIdToGqlCategory[section.id]
              const stepTasks = gqlCategory ? categories[gqlCategory] || [] : []
              const hasTasks = stepTasks.length > 0
              const pendingCount = stepTasks.filter(
                task =>
                  task.status === TaskStatus.New &&
                  !submittedTaskIds.has(task.id)
              ).length
              const hasPending = pendingCount > 0

              const stateConfig =
                sectionStateConfig[getSectionState(hasPending)]

              return (
                <ExpandableSection
                  key={section.id}
                  title={section.title}
                  startIcon={section.icon}
                  badgeCount={hasPending ? pendingCount.toString() : ''}
                  expandable={hasTasks}
                  expandButtonLabel={
                    <Tag size="small" variant={stateConfig.tagVariant}>
                      {stateConfig.statusLabel}
                    </Tag>
                  }
                  open={false}
                  secondaryTitle={stateConfig.description}
                >
                  {hasTasks ? (
                    <div className="flex flex-col gap-1">
                      {stepTasks.map(task => (
                        <TaskItem
                          key={task.id}
                          task={task}
                          individuals={individuals}
                          isSubmitted={submittedTaskIds.has(task.id)}
                          onSubmitted={handleTaskSubmitted}
                        />
                      ))}
                    </div>
                  ) : null}
                </ExpandableSection>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}
